import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "../../../core/common/header/PageHeader";
import {
  FaHome,
  FaClipboardList,
  FaSpinner,
  FaEdit,
  FaUsers,
  FaChartBar,
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
} from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Assignment = {
  id: number;
  examSetId: number;
  examSetName: string;
  level: string;
  variant: string;
  batchId: number;
  batchName: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  totalDuration: number;
  totalStudents: number;
  totalAttempts: number;
  pendingGrading: number;
  completedGrading: number;
};

const MentorExamManagement: React.FC = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeLevel, setActiveLevel] = useState<string>("ALL");

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/exams/assignments`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAssignments(data);
      } else {
        setError("Failed to load assignments");
      }
    } catch (err) {
      console.error("Error fetching assignments:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatus = (assignment: Assignment) => {
    const now = new Date();
    const start = new Date(assignment.scheduledStartTime);
    const end = new Date(assignment.scheduledEndTime);

    if (now < start) {
      return { label: "UPCOMING", color: "yellow", icon: FaClock };
    } else if (now >= start && now <= end) {
      return { label: "ACTIVE", color: "blue", icon: FaExclamationCircle };
    } else {
      return { label: "COMPLETED", color: "gray", icon: FaCheckCircle };
    }
  };

  // Filter by level
  const filteredAssignments =
    activeLevel === "ALL"
      ? assignments
      : assignments.filter((a) => a.level === activeLevel);

  // Group by level
  const levels = ["A1", "A2", "B1", "B2"];
  const groupedAssignments = levels.reduce((acc, level) => {
    acc[level] = assignments.filter((a) => a.level === level);
    return acc;
  }, {} as Record<string, Assignment[]>);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaClipboardList />}
        title="Manage Exams"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/mentor/dashboard" },
          { label: "Manage Exams" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">
                  Total Assignments
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {assignments.length}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaClipboardList className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">
                  Total Attempts
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {assignments.reduce((sum, a) => sum + a.totalAttempts, 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FaUsers className="text-purple-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">
                  Pending Grading
                </p>
                <p className="text-3xl font-bold text-orange-600 mt-1">
                  {assignments.reduce((sum, a) => sum + a.pendingGrading, 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <FaClock className="text-orange-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Completed</p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  {assignments.reduce((sum, a) => sum + a.completedGrading, 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FaCheckCircle className="text-green-600 text-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Level Filter Tabs */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => setActiveLevel("ALL")}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeLevel === "ALL"
                ? "bg-[#009fe3] text-white shadow-lg"
                : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            All Levels ({assignments.length})
          </button>
          {levels.map((level) => (
            <button
              key={level}
              onClick={() => setActiveLevel(level)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeLevel === level
                  ? "bg-[#009fe3] text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              {level} ({groupedAssignments[level].length})
            </button>
          ))}
        </div>

        {/* Assignments Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {error && (
            <div className="p-4 bg-red-50 border-b border-red-200 text-red-700">
              {error}
            </div>
          )}

          {filteredAssignments.length === 0 ? (
            <div className="text-center py-12">
              <FaClipboardList className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No Exam Assignments
              </h3>
              <p className="text-gray-500 text-sm">
                {activeLevel === "ALL"
                  ? "No exams have been assigned to your batches yet"
                  : `No ${activeLevel} level exams assigned`}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                      Exam
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Level
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                      Batch
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Schedule
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Progress
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredAssignments.map((assignment, index) => {
                    const status = getStatus(assignment);
                    const StatusIcon = status.icon;

                    return (
                      <tr
                        key={assignment.id}
                        className={`${
                          index % 2 === 1 ? "bg-gray-50" : "bg-white"
                        } hover:bg-blue-50 transition-colors`}
                      >
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium text-gray-900">
                              {assignment.examSetName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {assignment.variant}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                            {assignment.level}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FaUsers className="text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {assignment.batchName}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {assignment.totalStudents} students
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs text-gray-600">
                            <div>
                              <strong>Start:</strong>{" "}
                              {formatDate(assignment.scheduledStartTime)}
                            </div>
                            <div className="mt-1">
                              <strong>End:</strong>{" "}
                              {formatDate(assignment.scheduledEndTime)}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-center">
                            <div className="text-sm font-semibold text-gray-900">
                              {assignment.totalAttempts} /{" "}
                              {assignment.totalStudents}
                            </div>
                            <div className="text-xs text-gray-500">
                              attempts
                            </div>
                            <div className="mt-2">
                              <div className="text-xs text-orange-600 font-semibold">
                                Pending: {assignment.pendingGrading}
                              </div>
                              <div className="text-xs text-green-600 font-semibold">
                                Graded: {assignment.completedGrading}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                              status.color === "blue"
                                ? "bg-blue-100 text-blue-800"
                                : status.color === "yellow"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            <StatusIcon size={12} />
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() =>
                                navigate(`/mentor/exams/${assignment.id}/grade`)
                              }
                              className="inline-flex items-center justify-center gap-1 px-3 py-2 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors text-sm font-semibold"
                            >
                              <FaEdit size={14} />
                              Grade
                            </button>
                            <button
                              onClick={() =>
                                navigate(
                                  `/mentor/exams/${assignment.id}/results`
                                )
                              }
                              className="inline-flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-semibold"
                            >
                              <FaChartBar size={14} />
                              Results
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Refresh Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={fetchAssignments}
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" />
                Refreshing...
              </>
            ) : (
              "Refresh"
            )}
          </button>
        </div>
      </main>
    </div>
  );
};

export default MentorExamManagement;
